import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { documentViewerKind } from "@volca/ui/components/ui/document-viewer";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useDocumentDownload } from "@/profitability/documents/use-document-download";
import { MetaList } from "@/profitability/meta-list";
import { documentsHref } from "./source-links";
import { citationRow } from "./sourced";
import type { RunCitation } from "./types";

// "Table 1" is the reader's name for a CSV's only table: it says nothing.
const GENERIC_TABLE = /^Table \d+$/;

/**
 * The file's name and the place being looked at in it (what the template
 * calls the input, the sheet, the page, the row as the grid numbers it, the
 * column), with "Open in Documents" at the same place and "Download
 * original".
 */
export const SourceDocumentHeader = ({
  businessId,
  reportId,
  runId,
  compare,
  citation,
  onLeave,
}: {
  businessId: string;
  reportId: string;
  runId: string;
  compare: string | null;
  citation: RunCitation;
  onLeave: () => void;
}) => {
  const kind = documentViewerKind({
    contentType: citation.content_type,
    fileName: citation.file_name,
  });
  const { download, downloading } = useDocumentDownload(
    businessId,
    citation.document_id,
  );
  const sheet =
    citation.sheet_name && !GENERIC_TABLE.test(citation.sheet_name)
      ? citation.sheet_name
      : null;
  return (
    <Stack direction="row" gap={3} align="start" justify="between" wrap>
      <Stack gap={0.5} align="stretch" className="min-w-0 flex-1 basis-60">
        <Text variant="title" className="[overflow-wrap:anywhere]">
          {citation.file_name}
        </Text>
        <Text variant="caption" tone="muted">
          <MetaList>
            {citation.input_title}
            {sheet}
            {citation.page && `Page ${citation.page}`}
            {citation.row !== null && `Row ${citationRow(citation)}`}
            {citation.column}
          </MetaList>
        </Text>
      </Stack>
      <Stack direction="row" gap={1} align="center" wrap className="shrink-0">
        <Button
          variant="ghost"
          size="sm"
          nativeButton={false}
          render={
            <Link
              to={documentsHref({ reportId, runId, compare, citation, kind })}
              onClick={onLeave}
            />
          }
        >
          <ArrowSquareOutIcon aria-hidden data-icon="inline-start" />
          Open in Documents
        </Button>
        <Button
          variant="ghost"
          size="sm"
          loading={downloading}
          onClick={download}
        >
          <DownloadSimpleIcon aria-hidden data-icon="inline-start" />
          Download original
        </Button>
      </Stack>
    </Stack>
  );
};
